/**
 * One decode of the access token, shared by every consumer (BON-558).
 *
 * Permissions and persona are both read off the SAME token, and they have to
 * be the same token *instance*: the SPA renews access tokens silently, so two
 * independent `getAccessTokenSilently()` calls can straddle a renewal and hand
 * back grants from one token and roles from another. A viewer would then be
 * rendered chrome their permissions do not back — the Unauthorized landing
 * this ticket exists to remove.
 *
 * The claims therefore live in a module-level store rather than in per-hook
 * state: the first mounted consumer performs the fetch, every other consumer
 * subscribes to that one result. Same shape as `personaView`, including the
 * test-reset seam.
 */
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useSyncExternalStore } from 'react';

import { decodeJwtPayload } from '../auth/jwt-claims';

export interface TokenClaimsState {
  /** Decoded access-token claims, or `null` when there is no usable token —
   *  unauthenticated, the silent renewal failed, or the token was malformed. */
  claims: Record<string, unknown> | null;
  /** True until the token has either decoded or definitively failed. */
  loading: boolean;
}

const LOADING: TokenClaimsState = { claims: null, loading: true };
const NO_TOKEN: TokenClaimsState = { claims: null, loading: false };

let snapshot: TokenClaimsState = LOADING;
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let generation = 0;

function publish(next: TokenClaimsState) {
  if (snapshot.claims === next.claims && snapshot.loading === next.loading) return;
  snapshot = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): TokenClaimsState {
  return snapshot;
}

/**
 * Abandon whatever is in flight. Sign-out clears the store, but a
 * `getAccessTokenSilently()` call that STARTED before it resolves afterwards,
 * and this is a module-level store: publishing then would hand the signed-out
 * viewer's claims to every mounted consumer at once — permissions re-granted,
 * `RequirePermission` re-opened — with no effect left to run and correct it.
 * The generation bump is what makes that late result land in the bin. Today
 * production only survives it because `logout()` is a full-document redirect
 * that destroys the module; nothing in our code prevents it.
 */
function abandonInFlight() {
  generation += 1;
  inFlight = null;
}

function loadOnce(getToken: () => Promise<string>): Promise<void> {
  if (inFlight) return inFlight;
  const started = generation;
  inFlight = (async () => {
    try {
      const claims = decodeJwtPayload(await getToken());
      if (started === generation) publish({ claims, loading: false });
    } catch {
      // A rejection is NOT cached. Left in `inFlight`, one transient failure
      // (a network blip, `login_required` on a renewal) floors every persona
      // to `staff` and empties every permission for the rest of the page
      // session, silently — `rolesClaimMissing` needs a decoded token, so not
      // even the banner fires. Clearing it lets the next consumer to mount
      // retry, which is what per-hook fetching used to give us for free.
      if (started !== generation) return;
      inFlight = null;
      publish(NO_TOKEN);
    }
  })();
  return inFlight;
}

/** The decoded access-token claims. Every consumer sees one decode of one
 *  token — see the module note for why that matters. */
export function useTokenClaims(): TokenClaimsState {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const state = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (!isAuthenticated) {
      abandonInFlight();
      publish(NO_TOKEN);
      return;
    }
    loadOnce(() =>
      getAccessTokenSilently({
        authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
      }),
    );
  }, [isAuthenticated, getAccessTokenSilently]);

  return state;
}

/** Test seam. Leaves `listeners` alone for the same reason
 *  `resetPersonaViewForTests` does — unmounting is what removes a listener. */
export function resetTokenClaimsForTests() {
  snapshot = LOADING;
  abandonInFlight();
}
